import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import DnsIcon from "@mui/icons-material/Dns";
import PersonIcon from "@mui/icons-material/Person";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const QUICK_PORTS = [25, 465, 587, 2525];

const EmailSettings = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);

  const [settings, setSettings] = useState({
    smtp_host: "",
    smtp_port: "587",
    smtp_security: "TLS",
    smtp_require_auth: true,
    smtp_username: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_from_name: "",
  });

  const [testEmail, setTestEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, msg }

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    const res = await hitAxios({
      path: "/api/settings/get-all",
      showSnackbar: false,
    });
    if (res?.data?.success) {
      const d = res.data.data;
      setSettings((prev) => ({
        ...prev,
        smtp_host: d.smtp_host || "",
        smtp_port: d.smtp_port || "587",
        smtp_security: d.smtp_security || "TLS",
        smtp_require_auth: d.smtp_require_auth !== "false",
        smtp_username: d.smtp_username || "",
        smtp_password: d.smtp_password || "",
        smtp_from_email: d.smtp_from_email || "",
        smtp_from_name: d.smtp_from_name || "",
      }));
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    await hitAxios({
      path: "/api/settings/update",
      post: true,
      admin: true,
      obj: {
        ...settings,
        smtp_require_auth: String(settings.smtp_require_auth),
      },
    });
    setSaving(false);
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    setTesting(true);
    setTestResult(null);
    const res = await hitAxios({
      path: "/api/settings/send-test-email",
      post: true,
      admin: true,
      obj: { to: testEmail },
      showSnackbar: false,
    });
    setTesting(false);
    setTestResult({
      success: res?.data?.success,
      msg:
        res?.data?.msg ||
        (res?.data?.success ? "Email sent!" : "Failed to send email."),
    });
  };

  const fromPreview =
    settings.smtp_from_name && settings.smtp_from_email
      ? `"${settings.smtp_from_name}" <${settings.smtp_from_email}>`
      : settings.smtp_from_email || "";

  return (
    <Box>
      <PageHeader
        title={lang.emailSettings || "Email Settings"}
        subtitle={lang.emailSettingsDesc || "Manage SMTP configuration"}
        icon={EmailIcon}
        primaryAction={
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={saveSettings}
            disabled={saving}
            sx={{ borderRadius: 2 }}
          >
            {saving
              ? lang.saving || "Saving..."
              : lang.saveConfiguration || "Save Configuration"}
          </Button>
        }
      />

      <Grid container spacing={3}>
        {/* ── Left Column ── */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Stack spacing={3}>
            {/* SERVER CONFIGURATION */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  mb={0.5}
                >
                  <DnsIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {lang.serverConfiguration || "Server Configuration"}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={2.5}
                >
                  {lang.serverConfigurationDesc ||
                    "Configure your outgoing mail server"}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={2.5}>
                  {/* SMTP Host */}
                  <TextField
                    fullWidth
                    size="small"
                    label={lang.smtpHost || "SMTP Host"}
                    placeholder="xxxx.xxxxxxxx.com"
                    value={settings.smtp_host}
                    onChange={(e) =>
                      setSettings({ ...settings, smtp_host: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DnsIcon
                            sx={{ fontSize: 16, color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Security + Port */}
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 7 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {lang.securityProtocol || "Security Protocol"}
                        </InputLabel>
                        <Select
                          value={settings.smtp_security}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              smtp_security: e.target.value,
                              smtp_port:
                                e.target.value === "TLS"
                                  ? "587"
                                  : e.target.value === "SSL"
                                    ? "465"
                                    : "25",
                            })
                          }
                          label={lang.securityProtocol || "Security Protocol"}
                        >
                          <MenuItem value="TLS">
                            TLS ({lang.recommended || "Recommended"})
                          </MenuItem>
                          <MenuItem value="SSL">SSL</MenuItem>
                          <MenuItem value="None">
                            {lang.none || "None"}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={lang.port || "Port"}
                        value={settings.smtp_port}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            smtp_port: e.target.value,
                          })
                        }
                        helperText={
                          settings.smtp_security === "TLS"
                            ? lang.tlsUsesPort || "TLS typically uses 587"
                            : settings.smtp_security === "SSL"
                              ? lang.sslUsesPort || "SSL typically uses 465"
                              : ""
                        }
                      />
                    </Grid>
                  </Grid>

                  {/* Quick Select Ports */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {lang.quickSelect || "Quick select"}:
                    </Typography>
                    {QUICK_PORTS.map((port) => (
                      <Chip
                        key={port}
                        label={port}
                        size="small"
                        onClick={() =>
                          setSettings({
                            ...settings,
                            smtp_port: String(port),
                          })
                        }
                        color={
                          settings.smtp_port === String(port)
                            ? "primary"
                            : "default"
                        }
                        variant={
                          settings.smtp_port === String(port)
                            ? "filled"
                            : "outlined"
                        }
                        sx={{ cursor: "pointer", fontWeight: 600 }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* AUTHENTICATION */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  mb={0.5}
                >
                  <LockIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {lang.authentication || "Authentication"}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2.5, mt: 1.5 }} />

                <Stack spacing={2.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.smtp_require_auth}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            smtp_require_auth: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={600}>
                        {lang.requireAuthentication || "Require Authentication"}
                      </Typography>
                    }
                  />

                  {settings.smtp_require_auth && (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        label={lang.username || "Username"}
                        placeholder="xxxxx@xxxxxxx.com"
                        value={settings.smtp_username}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            smtp_username: e.target.value,
                          })
                        }
                        helperText={
                          lang.usernameHelper ||
                          "Leave empty to use Sender Email as username"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon
                                sx={{ fontSize: 16, color: "text.disabled" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        label={
                          lang.passwordAppPassword || "Password / App Password"
                        }
                        type={showPassword ? "text" : "password"}
                        value={settings.smtp_password}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            smtp_password: e.target.value,
                          })
                        }
                        helperText={
                          lang.gmailAppPasswordHelper ||
                          "For Gmail use an App Password instead of your account password"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon
                                sx={{ fontSize: 16, color: "text.disabled" }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setShowPassword((v) => !v)}
                              >
                                {showPassword ? (
                                  <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <VisibilityIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* ── Right Column ── */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={3}>
            {/* SENDER */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  mb={0.5}
                >
                  <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {lang.sender || "Sender"}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2.5, mt: 1.5 }} />

                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label={lang.senderEmail || "Sender Email"}
                    placeholder="xxxx@xxxxxxxx.com"
                    value={settings.smtp_from_email}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        smtp_from_email: e.target.value,
                      })
                    }
                    helperText={
                      lang.senderEmailHelper ||
                      "The email address emails will be sent from"
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon
                            sx={{ fontSize: 16, color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label={
                      lang.fromNameDisplayName || "From Name (Display Name)"
                    }
                    placeholder="Codeyon IT Services"
                    value={settings.smtp_from_name}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        smtp_from_name: e.target.value,
                      })
                    }
                  />

                  {fromPreview && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: "action.hover",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        display="block"
                        mb={0.5}
                      >
                        {lang.preview || "Preview"}:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {fromPreview}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* SEND TEST EMAIL */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  mb={0.5}
                >
                  <SendIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {lang.sendTestEmail || "Send Test Email"}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={2}
                >
                  {lang.sendTestEmailDesc ||
                    "Save your configuration first, then send a test to verify everything works."}
                </Typography>
                <Divider sx={{ mb: 2.5 }} />

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label={lang.recipientEmail || "Recipient Email"}
                    placeholder="you@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendTestEmail()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SendIcon
                            sx={{ fontSize: 16, color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={
                      testing ? (
                        <CircularProgress size={15} color="inherit" />
                      ) : (
                        <SendIcon />
                      )
                    }
                    onClick={sendTestEmail}
                    disabled={testing || !testEmail}
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    {testing
                      ? lang.sending || "Sending..."
                      : lang.send || "Send"}
                  </Button>

                  {testResult && (
                    <Alert
                      severity={testResult.success ? "success" : "error"}
                      sx={{ borderRadius: 2 }}
                      onClose={() => setTestResult(null)}
                    >
                      {testResult.msg}
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailSettings;
